/**
 * Chat Service - TROIA V3
 * Service layer para comunicação com API de conversas e mensagens
 */

import { api, apiGet, apiPost, apiPut, apiDelete } from '@/lib/api';
import { getApiBaseUrl } from '@/lib/config';
import type {
  // Conversation types from @troia-v3/types
  ConversationResponse,
  ConversationListResponse,
  ConversationHistoryGroupListResponse,
  ConversationQuery,
  ConversationMediaQuery,
  ConversationMediaListResponse,
  CreateConversationRequest,
  UpdateConversationRequest,
  TransferRequest,
  CloseConversationRequest,

  // Kanban de atendimentos (SP4) types from @troia-v3/types
  KanbanBoardResponse,
  KanbanColumnPage,
  ConversationKanbanBoardAggregates,
  ConversationKanbanCard,
  ConversationKanbanColumnQuery,
  ConversationKanbanQuery,

  // Message types from @troia-v3/types
  ConversationMessageResponse,
  ConversationMessageListResponse,
  ConversationMessageQuery,
  SendMessageRequest,
  EditMessageRequest,
  ForwardMessageRequest,
  ForwardMessageResponse,
  AddReactionRequest,
  RemoveReactionRequest,
  MarkAsReadRequest,
  DeleteMessageRequest,
  MessageSearchRequest,
  MessageSearchResponse,
  BulkMessageOperationRequest,
} from '@troia-v3/types';

// ============================================================================
// Conversations API
// ============================================================================

/**
 * Buscar todas as conversas com filtros e paginação
 */
export const getConversations = async (
  query?: ConversationQuery,
  signal?: AbortSignal
): Promise<ConversationListResponse> => {
  const params = new URLSearchParams();

  if (query?.page) params.append('page', query.page.toString());
  if (query?.limit) params.append('limit', query.limit.toString());

  // Aplicar filtros
  if (query?.filters) {
    Object.entries(query.filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          value.forEach(v => params.append(`filters[${key}]`, v.toString()));
        } else {
          params.append(`filters[${key}]`, value.toString());
        }
      }
    });
  }

  return apiGet<ConversationListResponse>(
    `/conversations?${params.toString()}`,
    { signal }
  );
};

/**
 * Buscar conversa por ID
 */
export const getConversationById = async (
  id: string,
  signal?: AbortSignal
): Promise<ConversationResponse> => {
  return apiGet<ConversationResponse>(`/conversations/${id}`, { signal });
};

/**
 * Galeria de mídia paginada da conversa.
 * Substitui o antigo `conversation.files` (que era agregado inteiro no documento
 * da conversa e estourava o limite de 16MB do MongoDB). Retorna itens paginados
 * + contagens por tipo.
 */
export const getConversationMedia = async (
  id: string,
  params?: ConversationMediaQuery,
  signal?: AbortSignal
): Promise<ConversationMediaListResponse> => {
  const search = new URLSearchParams();
  if (params?.type) search.append('type', params.type);
  if (params?.page) search.append('page', params.page.toString());
  if (params?.limit) search.append('limit', params.limit.toString());
  const qs = search.toString();
  return apiGet<ConversationMediaListResponse>(
    `/conversations/${id}/media${qs ? `?${qs}` : ''}`,
    { signal }
  );
};

/**
 * Criar nova conversa
 */
export const createConversation = async (
  data: CreateConversationRequest,
  signal?: AbortSignal
): Promise<ConversationResponse> => {
  return apiPost<ConversationResponse>('/conversations', data, { signal });
};

/**
 * Atualizar conversa
 */
export const updateConversation = async (
  id: string,
  data: UpdateConversationRequest,
  signal?: AbortSignal
): Promise<ConversationResponse> => {
  return apiPut<ConversationResponse>(`/conversations/${id}`, data, { signal });
};

/**
 * Transferir conversa — atribuir a usuário/equipe ou desatribuir
 *
 * Endpoint padronizado: `POST /conversations/:id/transfer`
 * Body: `{ toUserId?, toTeamId?, reason?, notes? }` — XOR entre user/team, null desatribui.
 */
export const transferConversation = async (
  id: string,
  data: TransferRequest,
  signal?: AbortSignal
): Promise<ConversationResponse> => {
  return apiPost<ConversationResponse>(`/conversations/${id}/transfer`, data, { signal });
};

/**
 * Fechar conversa
 */
export const closeConversation = async (
  id: string,
  data?: CloseConversationRequest,
  signal?: AbortSignal
): Promise<ConversationResponse> => {
  return apiPost<ConversationResponse>(`/conversations/${id}/close`, data, { signal });
};

/**
 * Reabrir conversa
 */
export const reopenConversation = async (
  id: string,
  signal?: AbortSignal
): Promise<ConversationResponse> => {
  return apiPost<ConversationResponse>(`/conversations/${id}/reopen`, {}, { signal });
};

/**
 * Silenciar conversa — suprime push notifications do usuário atual
 * para `CONVERSATION_MESSAGE_RECEIVED`. Response vem com `muted: true`.
 */
export const muteConversation = async (
  id: string,
  signal?: AbortSignal
): Promise<ConversationResponse> => {
  return apiPost<ConversationResponse>(`/conversations/${id}/mute`, {}, { signal });
};

/**
 * Dessilenciar conversa. Response vem com `muted: false`.
 */
export const unmuteConversation = async (
  id: string,
  signal?: AbortSignal
): Promise<ConversationResponse> => {
  return apiPost<ConversationResponse>(`/conversations/${id}/unmute`, {}, { signal });
};

/**
 * Histórico agrupado por (contato, canal) — só conversas fechadas.
 * Uma linha por par, representada pela conversa mais recente + conversationCount.
 */
export const getConversationHistoryGroups = async (
  query?: { page?: number; limit?: number; search?: string },
  signal?: AbortSignal
): Promise<ConversationHistoryGroupListResponse> => {
  const params = new URLSearchParams();
  if (query?.page) params.append('page', query.page.toString());
  if (query?.limit) params.append('limit', query.limit.toString());
  if (query?.search) params.append('filters[search]', query.search);

  return apiGet<ConversationHistoryGroupListResponse>(
    `/conversations/history?${params.toString()}`,
    { signal }
  );
};

/**
 * Timeline de mensagens do histórico do par (contato, canal): mensagens de
 * TODAS as conversas fechadas, intercaladas por sentAt desc (mesma convenção
 * da listagem normal de mensagens do chat).
 */
export const getConversationHistoryMessages = async (
  contactId: string,
  channelId: string,
  query?: { page?: number; limit?: number },
  signal?: AbortSignal
): Promise<ConversationMessageListResponse> => {
  const params = new URLSearchParams();
  if (query?.page) params.append('page', query.page.toString());
  if (query?.limit) params.append('limit', query.limit.toString());

  return apiGet<ConversationMessageListResponse>(
    `/conversations/history/${contactId}/${channelId}/messages?${params.toString()}`,
    { signal }
  );
};

/**
 * Todas as conversas fechadas de um contato em um canal, paginadas por recência.
 */
export const getConversationHistoryByContactAndChannel = async (
  contactId: string,
  channelId: string,
  query?: { page?: number; limit?: number },
  signal?: AbortSignal
): Promise<ConversationListResponse> => {
  const params = new URLSearchParams();
  if (query?.page) params.append('page', query.page.toString());
  if (query?.limit) params.append('limit', query.limit.toString());

  return apiGet<ConversationListResponse>(
    `/conversations/history/${contactId}/${channelId}?${params.toString()}`,
    { signal }
  );
};

// ============================================================================
// Kanban de atendimentos (SP4 — 2026-07-16)
//
// Diferente do resto do service (helpers `apiGet`/`apiPost`, sem suporte a
// `params` estruturado), estes 2 métodos usam `api.get(url, { params })`
// direto — mesma serialização (axios nested params → `filters[key]=value`)
// usada por `ticketsService.getKanbanBoard`/`getKanbanColumn` (espelho SP3).
// ============================================================================

/**
 * Board kanban de atendimentos: janelas por lane (5 raias fixas — ai, queue,
 * inService, transferred, closed) + counts reais. Visibilidade idêntica à
 * listagem normal de conversas (D-context-gate aplicado no service).
 */
export const getAttendanceKanbanBoard = async (
  params: ConversationKanbanQuery,
): Promise<KanbanBoardResponse<ConversationKanbanCard, ConversationKanbanBoardAggregates>> => {
  const response = await api.get('conversations/kanban', { params });
  return response.data.data;
};

/**
 * Próxima página de UMA lane do kanban de atendimentos, via cursor opaco.
 */
export const getAttendanceKanbanColumn = async (
  params: ConversationKanbanColumnQuery,
): Promise<KanbanColumnPage<ConversationKanbanCard>> => {
  const response = await api.get('conversations/kanban/column', { params });
  return response.data.data;
};

/*
 * `GET /conversations/sla-thresholds` NÃO tem cliente aqui, de propósito
 * (revisão final do Plano D). O card do board já recebe o snapshot MEDIDO
 * dentro de cada card do kanban (`ConversationKanbanCard.sla`), então uma
 * segunda consulta por limiares seria dado duplicado — e o cliente do hook que
 * existia nunca foi montado por tela nenhuma. A ROTA continua viva no backend
 * (API pública, com teste E2E): quando uma tela precisar dos limiares fora do
 * board, o cliente nasce aqui.
 */

// ============================================================================
// Messages API
// ============================================================================

/**
 * Buscar mensagens de uma conversa
 */
export const getMessages = async (
  query: ConversationMessageQuery,
  signal?: AbortSignal
): Promise<ConversationMessageListResponse> => {
  const params = new URLSearchParams();

  if (query.conversationId) params.append('conversationId', query.conversationId);
  if (query.page) params.append('page', query.page.toString());
  if (query.limit) params.append('limit', query.limit.toString());

  // Aplicar filtros
  if (query.filters) {
    Object.entries(query.filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          value.forEach(v => params.append(`filters[${key}]`, v.toString()));
        } else {
          params.append(`filters[${key}]`, value.toString());
        }
      }
    });
  }

  return apiGet<ConversationMessageListResponse>(
    `/conversation-messages?${params.toString()}`,
    { signal }
  );
};

/**
 * Buscar mensagem por ID
 */
export const getMessageById = async (
  id: string,
  signal?: AbortSignal
): Promise<ConversationMessageResponse> => {
  return apiGet<ConversationMessageResponse>(`/conversation-messages/${id}`, { signal });
};

/**
 * Enviar mensagem
 *
 * `handleErrorLocally` (default `false`, extra item do review 2026-07-27):
 * quando `true`, suprime o toast genérico do interceptor global
 * (`lib/api.ts`) — usado por `useSendMessage` (ChatBox.tsx), cujo próprio
 * `catch` já mostra um toast específico pra CADA branch de erro (janela
 * fechada via `handleMessagingWindowClosedError`, ou o genérico "Erro ao
 * enviar mensagem"). Sem isso, os dois toasts apareciam juntos no 422.
 * Default `false` preserva o comportamento de outros callers que NÃO têm
 * tratamento local (ex.: `emailService.sendEmail`, que depende do toast
 * global pra reportar falha de envio).
 */
export const sendMessage = async (
  data: SendMessageRequest,
  signal?: AbortSignal,
  handleErrorLocally?: boolean
): Promise<ConversationMessageResponse> => {
  return apiPost<ConversationMessageResponse>('/conversation-messages/send', data, {
    signal,
    ...(handleErrorLocally ? { _handleErrorLocally: true } : {}),
  });
};

/**
 * Enviar mensagem com arquivo (com suporte a progress tracking)
 */
export const sendMessageWithFile = async (
  conversationId: string,
  file: File,
  caption?: string,
  replyToMessageId?: string,
  onProgress?: (progress: number) => void,
  signal?: AbortSignal
): Promise<ConversationMessageResponse> => {
  const formData = new FormData();
  formData.append('conversationId', conversationId);
  formData.append('file', file);
  if (caption) formData.append('caption', caption);
  if (replyToMessageId) formData.append('replyToMessageId', replyToMessageId);

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    // Progress tracking
    if (onProgress) {
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100);
          onProgress(progress);
        }
      });
    }

    // Success
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const result = JSON.parse(xhr.responseText);
          resolve(result.data);
        } catch (error) {
          reject(new Error('Invalid response format'));
        }
      } else {
        // Propagar a mensagem real do backend (ex: limite de tamanho por canal,
        // permissão) — sem isso o atendente só via "HTTP error! status: 422"
        let message = `HTTP error! status: ${xhr.status}`;
        try {
          const body = JSON.parse(xhr.responseText) as { message?: string };
          if (body.message) {
            message = body.message;
          }
        } catch {
          // responseText não é JSON (ex: 413 do proxy) — mantém a genérica
        }
        reject(new Error(message));
      }
    });

    // Error
    xhr.addEventListener('error', () => {
      reject(new Error('Upload failed'));
    });

    // Abort
    xhr.addEventListener('abort', () => {
      reject(new Error('Upload aborted'));
    });

    // Handle signal abort
    if (signal) {
      signal.addEventListener('abort', () => {
        xhr.abort();
      });
    }

    // Start upload
    const apiBaseUrl = getApiBaseUrl();
    xhr.open('POST', `${apiBaseUrl}/conversation-messages/send-media`);

    const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    }

    xhr.send(formData);
  });
};

/**
 * Editar mensagem
 */
export const editMessage = async (
  id: string,
  data: EditMessageRequest,
  signal?: AbortSignal
): Promise<ConversationMessageResponse> => {
  return apiPut<ConversationMessageResponse>(`/conversation-messages/${id}/edit`, data, { signal });
};

/**
 * Encaminhar mensagem — retorna ForwardMessageResponse com successful/failed por contato.
 */
export const forwardMessage = async (
  data: ForwardMessageRequest,
  signal?: AbortSignal
): Promise<ForwardMessageResponse> => {
  return apiPost<ForwardMessageResponse>('/conversation-messages/forward', data, { signal });
};

/**
 * Adicionar reação a mensagem
 */
export const addReaction = async (
  data: AddReactionRequest,
  signal?: AbortSignal
): Promise<ConversationMessageResponse> => {
  return apiPost<ConversationMessageResponse>('/conversation-messages/reactions/add', data, { signal });
};

/**
 * Remover reação de mensagem
 */
export const removeReaction = async (
  data: RemoveReactionRequest,
  signal?: AbortSignal
): Promise<void> => {
  return apiPost<void>('/conversation-messages/reactions/remove', data, { signal });
};

/**
 * Marcar mensagens como lidas
 */
export const markAsRead = async (
  data: MarkAsReadRequest,
  signal?: AbortSignal
): Promise<void> => {
  return apiPost<void>('/conversation-messages/mark-as-read', data, { signal });
};

/**
 * Deletar mensagem
 */
export const deleteMessage = async (
  id: string,
  data?: DeleteMessageRequest,
  signal?: AbortSignal
): Promise<void> => {
  const body = data?.deleteForEveryone ? { deleteForEveryone: true } : undefined;
  return apiDelete(`/conversation-messages/${id}`, {
    ...(signal ? { signal } : {}),
    ...(body ? { data: body } : {}),
  });
};

/**
 * Buscar mensagens
 */
export const searchMessages = async (
  data: MessageSearchRequest,
  signal?: AbortSignal
): Promise<MessageSearchResponse> => {
  return apiPost<MessageSearchResponse>('/conversation-messages/search', data, { signal });
};

/**
 * Operação em lote para mensagens
 */
export const bulkMessageOperation = async (
  data: BulkMessageOperationRequest,
  signal?: AbortSignal
): Promise<void> => {
  return apiPost<void>('/conversation-messages/bulk', data, { signal });
};

/**
 * Enviar mensagem template em uma conversa
 */
export const sendTemplate = async (
  data: { conversationId: string; templateId: string; variables: Record<string, string> },
  signal?: AbortSignal,
): Promise<ConversationMessageResponse> =>
  apiPost<ConversationMessageResponse>('/conversation-messages/send-template', data, { signal });

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Helper: Enviar mensagem de texto simples
 */
export const sendTextMessage = async (
  conversationId: string,
  text: string,
  replyToMessageId?: string,
  signal?: AbortSignal
): Promise<ConversationMessageResponse> => {
  return sendMessage({
    conversationId,
    content: [{ type: 'text', text }],
    replyToMessageId,
  }, signal);
};

/**
 * Helper: Marcar todas as mensagens de uma conversa como lidas
 */
export const markConversationAsRead = async (
  _conversationId: string,
  messageIds: string[],
  signal?: AbortSignal
): Promise<void> => {
  if (messageIds.length === 0) return;

  return markAsRead({ messageIds }, signal);
};

/**
 * ✅ Get total unread count for authenticated user (optimized endpoint)
 * Replaces heavy 100-conversation query with single aggregation
 */
export const getUnreadCount = async (
  signal?: AbortSignal
): Promise<number> => {
  const response = await apiGet<{ unreadCount: number }>(
    '/conversations/unread-count',
    { signal }
  );
  return response.unreadCount;
};

/**
 * ✅ Get unread count by conversation type (individual, group, ai)
 * Used for sidebar tab badges
 *
 * Note: 'ai' is a virtual filter translated by backend to: agentId EXISTS AND agentStatus='active'
 */
export const getUnreadCountByType = async (
  conversationType: 'individual' | 'group' | 'ai',
  signal?: AbortSignal
): Promise<number> => {
  const response = await apiGet<{ count: number }>(
    `/conversations/unread-count/${conversationType}`,
    { signal }
  );
  return response.count;
};

// Export all as default service object
const chatService = {
  // Conversations
  getConversations,
  getConversationById,
  getConversationMedia,
  getConversationHistoryGroups,
  getConversationHistoryByContactAndChannel,
  getConversationHistoryMessages,
  createConversation,
  updateConversation,
  transferConversation,
  closeConversation,
  reopenConversation,
  muteConversation,
  unmuteConversation,
  getUnreadCount,  // ✅ Novo método otimizado
  getUnreadCountByType,  // ✅ Contador por tipo de conversa
  getAttendanceKanbanBoard,
  getAttendanceKanbanColumn,

  // Messages
  getMessages,
  getMessageById,
  sendMessage,
  sendMessageWithFile,
  sendTextMessage,
  sendTemplate,
  editMessage,
  forwardMessage,
  addReaction,
  removeReaction,
  markAsRead,
  markConversationAsRead,
  deleteMessage,
  searchMessages,
  bulkMessageOperation,
};

export default chatService;
